import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Avatar,
  Typography,
  Card,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import {
  getAllEarnedLeaveRequests,
  updateEarnedLeaveRequestStatus,
  deleteEarnedLeaveRequest,
} from "../../services/earnedLeaveRequest.service";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import MasterCommonTable from "../../sharedComponents/MaterCommonTable/MasterCommonTable";
import dayjs from "dayjs";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import Styles from "../Employee/Employee.module.scss";
import ApplyEarnedLeave from "./ApplyEarnedLeave";
import { useAuth } from "../../context/AuthProvider";
import { jwtDecode } from "jwt-decode";
import TextArea from "antd/es/input/TextArea";
import { isAdminRole } from "../../utils/roles";

const EarnedLeaveRequests = () => {
  const { accessToken } = useAuth();
  const [data, setData] = useState([]);
  const [tableTotalCount, setTableTotalCount] = useState(0);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [openApplyDialog, setOpenApplyDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [userId, setUserId] = useState(null);
  const [activeTab, setActiveTab] = useState("Pending");
  const [filteredData, setFilteredData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  // Check if user is admin (role 1 = superadmin, role 4 = admin)
  useEffect(() => {
    if (accessToken) {
      try {
        const decodedToken = jwtDecode(accessToken);
        setIsAdmin(isAdminRole(decodedToken.role));
        setUserId(decodedToken.id);
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
  }, [accessToken]);

  // Set page title
  useEffect(() => {
    document.title = "Earned Leave Requests - LMT";
    return () => {
      document.title = "LMT";
    };
  }, []);

  const getData = async () => {
    try {
      const params = {
        userId: userId,
        isAdmin: isAdmin,
      };

      const res = await getAllEarnedLeaveRequests(params);
      const modifiedData = res?.data?.data.map((e) => ({
        ...e,
        employeeInfo: e.userName && e.employeeID ? `${e.userName} (${e.employeeID})` : e.userName || "-",
        dateFormatted: `${dayjs(e.date).format("DD/MM/YYYY")} (${dayjs(e.date).format("dddd")})`,
        leaveType: e.isHalfDay ? "Half Day" : "Full Day",
        rejectionReason: e.rejectionReason || "-",
        updatedByInfo: e.updatedByName && e.updatedByEmployeeID ? `${e.updatedByName}` : e.updatedByName || "-",
        createdAtFormatted: dayjs(e.createdAt).format("DD/MM/YYYY HH:mm"),
        isReverted: e.status === "Approved" ? true : false,
      }));
      setData(modifiedData || []);
      setTableTotalCount(modifiedData?.length || 0);
    } catch (error) {
      console.error("Error fetching data:", error);
      ToastError("Failed to fetch requests");
    }
  };

  useEffect(() => {
    if (userId || isAdmin) {
      getData();
    }
  }, [userId, isAdmin]);

  // Filter data based on active tab, month, and year
  useEffect(() => {
    let filtered = data;

    // Filter by month and year
    if (selectedMonth !== "All" || selectedYear) {
      filtered = filtered.filter((item) => {
        const itemDate = dayjs(item.date);
        const yearMatch = selectedYear ? itemDate.year() === parseInt(selectedYear) : true;
        const monthMatch = selectedMonth !== "All" ? itemDate.format("MM") === selectedMonth : true;
        return yearMatch && monthMatch;
      });
    }

    // Filter by status tab
    if (activeTab !== "All") {
      filtered = filtered.filter((item) => item.status === activeTab);
    }

    setFilteredData(filtered);
  }, [data, activeTab, selectedMonth, selectedYear]);

  const handleApprove = async (row) => {
    try {
      const payload = {
        dateId: row.dateId,
        status: "Approved",
      };
      const res = await updateEarnedLeaveRequestStatus(row._id, payload);
      ToastSuccess(res.message);
      getData();
    } catch (error) {
      ToastError(error?.data?.message || "Failed to approve request");
    }
  };

  const handleRejectClick = (row) => {
    setSelectedRequest(row);
    setRejectionReason("");
    setOpenRejectDialog(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      ToastError("Please provide a rejection reason");
      return;
    }

    try {
      const payload = {
        dateId: selectedRequest.dateId,
        status: "Rejected",
        rejectionReason: rejectionReason,
      };
      const res = await updateEarnedLeaveRequestStatus(
        selectedRequest._id,
        payload
      );
      ToastSuccess(res.message);
      setOpenRejectDialog(false);
      setSelectedRequest(null);
      setRejectionReason("");
      getData();
    } catch (error) {
      ToastError(error?.data?.message || "Failed to reject request");
    }
  };

  const handleDelete = async (row) => {
    try {
      const res = await deleteEarnedLeaveRequest(row._id);
      ToastError(res.message);
      getData();
    } catch (error) {
      ToastError(error?.data?.message || "Failed to delete request");
    }
  };

  const handleRevert = async (row) => {
    try {
      const payload = {
        dateId: row.dateId,
        status: "Reverted",
      };
      const res = await updateEarnedLeaveRequestStatus(row._id, payload);
      ToastSuccess(res.message);
      getData();
    } catch (error) {
      ToastError(error?.data?.message || "Failed to revert request");
    }
  };

  const columns = [
    {
      id: "employeeInfo",
      label: "Employee",
      minWidth: 200,
    },
    {
      id: "dateFormatted",
      label: "Weekend Date",
      minWidth: 150,
    },
    {
      id: "reason",
      label: "Reason",
      minWidth: 250,
    },
    {
      id: "leaveType",
      label: "Leave Type",
      minWidth: 120,
    },

  ];

  if (activeTab === "Rejected") {
    columns.push({
      id: "rejectionReason",
      label: "Rejection Reason",
      minWidth: 200,
    });
  }
  if (activeTab === "All") {
    columns.push({ id: "status", label: "Status", minWidth: 200, });
  }

  // Add Updated By column for Approved, Rejected, and Reverted tabs
  if (["Approved", "Rejected", "Reverted", "All"].includes(activeTab)) {
    columns.push({
      id: "updatedByInfo",
      label: "Updated By",
      minWidth: 180,
    });
  }

  columns.push({
    id: "createdAtFormatted",
    label: "Requested On",
    minWidth: 150,
  });

  const tableConfig = {
    isAction: true,
    isApprove: isAdmin,
    isReject: isAdmin,
    isRevert: isAdmin,
    isAdmin: isAdmin,
    isMore: true,
  };

  // Calculate statistics
  const stats = {
    total: data.length,
    pending: data.filter((r) => r.status === "Pending").length,
    approved: data.filter((r) => r.status === "Approved").length,
    rejected: data.filter((r) => r.status === "Rejected").length,
    reverted: data.filter((r) => r.status === "Reverted").length,
  };

  return (
    <Box className={Styles.Page}>
      {/* Row 1: Tabs and Filters */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        {/* Left: Tabs */}
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            minHeight: 'auto',
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              color: "#666",
              minHeight: 'auto',
              py: 1,
              minWidth: 'auto',
              px: 2
            },
            "& .Mui-selected": {
              color: "#008080 !important",
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#008080",
            },
          }}
        >
          <Tab label={`Pending (${stats.pending})`} value="Pending" />
          <Tab label={`Approved (${stats.approved})`} value="Approved" />
          <Tab label={`Rejected (${stats.rejected})`} value="Rejected" />
          <Tab label={`Reverted (${stats.reverted})`} value="Reverted" />
          <Tab label={`All (${stats.total})`} value="All" />
        </Tabs>

        {/* Right: Filters */}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: "4px",
              border: "1px solid #d5d7e8",
              outline: "none",
              fontSize: "13px",
              minWidth: "120px"
            }}
          >
            <option value="All">All Months</option>
            <option value="01">January</option>
            <option value="02">February</option>
            <option value="03">March</option>
            <option value="04">April</option>
            <option value="05">May</option>
            <option value="06">June</option>
            <option value="07">July</option>
            <option value="08">August</option>
            <option value="09">September</option>
            <option value="10">October</option>
            <option value="11">November</option>
            <option value="12">December</option>
          </select>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: "4px",
              border: "1px solid #d5d7e8",
              outline: "none",
              fontSize: "13px",
              minWidth: "80px"
            }}
          >
            {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </Box>
      </Box>

      {/* Row 2: Button */}
      {!isAdmin && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => setOpenApplyDialog(true)}
            sx={{
              textTransform: "none",
              borderRadius: 1,
              px: 2.5,
              py: 0.75,
              bgcolor: "#008080",
              "&:hover": { bgcolor: "#006666" },
              whiteSpace: 'nowrap'
            }}
          >
            Request Earned Leave
          </Button>
        </Box>
      )}

      <MasterCommonTable
        tableConfig={tableConfig}
        columns={columns}
        data={filteredData}
        tableTotalCount={filteredData.length}
        handleApprove={handleApprove}
        handleReject={handleRejectClick}
        handleRevert={handleRevert}
      />

      {/* Rejection Dialog */}
      <Dialog
        open={openRejectDialog}
        onClose={() => setOpenRejectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Earned Leave Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this request:
          </Typography>
          {selectedRequest && (
            <Box sx={{ mb: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Employee:</strong> {selectedRequest.userName}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {selectedRequest.dateFormatted} (
                {selectedRequest.dayName})
              </Typography>
              <Typography variant="body2">
                <strong>Reason:</strong> {selectedRequest.reason}
              </Typography>
            </Box>
          )}
          <TextArea
            autoFocus
            margin="dense"
            label="Rejection Reason"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Explain why this request is being rejected"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)}>Cancel</Button>
          <Button
            onClick={handleRejectConfirm}
            color="error"
            variant="contained"
            disabled={!rejectionReason.trim()}
          >
            Reject Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Apply Earned Leave Dialog - only for non-admins */}
      <Dialog
        open={openApplyDialog}
        onClose={() => setOpenApplyDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <ApplyEarnedLeave setOpenDialog={setOpenApplyDialog} GetData={getData} />
      </Dialog>
    </Box>
  );
};

export default EarnedLeaveRequests;
